import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Row from "antd/es/row";
import { CloseOutlined } from "@ant-design/icons";
import { withTranslation, TFunction } from "react-i18next";
import { Link } from "react-router-dom";
import Container from "../../common/Container";
import LoginModal from "../LoginModal";
import ProfileMenu from "./ProfileMenu";
import { useAuth } from "../../contexts/AuthContext";
import {
  HeaderSection,
  LogoContainer,
  Burger,
  MobileCluster,
  NotHidden,
  CustomNavLinkSmall,
  Outline,
  LogoTitle,
  LoginButton,
  LanguageToggleButton,
  DrawerHeader,
  DrawerClose,
  DrawerNav,
  DrawerNavLink,
  DrawerLoginButton,
  DrawerMask,
  DrawerPanel,
} from "./styles";

const DRAWER_NAV_ITEMS = [
  { to: "/", labelKey: "Home" },
  { to: "/explore", labelKey: "Explore" },
  { to: "/stories", labelKey: "Stories" },
] as const;

// Inlined so stroke="currentColor" inherits the button's text color
// (and its hover/focus states). Source: public/img/svg/language.svg.
const LanguageIcon = () => (
  <svg
    width="24"
    height="24"
    strokeWidth="1.5"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2 5H9M16 5H13.5M9 5L13.5 5M9 5V3M13.5 5C12.6795 7.73513 10.9612 10.3206 9 12.5929M4 17.5C5.58541 16.1411 7.376 14.4744 9 12.5929M9 12.5929C8 11.5 6.4 9.3 6 8.5M9 12.5929L12 15.5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 21L14.6429 18M21.5 21L20.3571 18M14.6429 18L17.5 10.5L20.3571 18M14.6429 18H20.3571"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Header = ({ t, i18n }: { t: TFunction; i18n: any }) => {
  const [visible, setVisibility] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const auth = useAuth();
  const openedAtRef = useRef(0);

  const openDrawer = () => {
    openedAtRef.current = Date.now();
    setVisibility(true);
  };

  // Defend against iOS "ghost click": the same tap that opens the drawer
  // can synthesize a second click landing on the freshly-rendered close
  // button or mask, immediately re-closing it.
  const closeDrawer = () => {
    if (Date.now() - openedAtRef.current < 350) return;
    setVisibility(false);
  };

  const openLogin = () => {
    setVisibility(false);
    setLoginOpen(true);
  };

  const switchLanguage = () => {
    i18n.changeLanguage(i18n.language === "en" ? "cn" : "en");
    closeDrawer();
  };

  // Body scroll lock + esc-to-close while drawer is open.
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setVisibility(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [visible]);

  const closeLogin = () => {
    setLoginOpen(false);
  };

  const AuthEntry = () =>
    auth.isAuthenticated ? (
      <ProfileMenu />
    ) : (
      <LoginButton type="button" onClick={openLogin}>
        {t("Log in")}
      </LoginButton>
    );

  const getBrandName = () => {
    return i18n.language === "cn" ? "CapCam 3D相机" : "CapCam";
  };

  const HomeMenuItem = () => (
    <CustomNavLinkSmall
      as={Link}
      to="/"
      style={{
        color: "#1d1d1f",
        fontWeight: "500",
        padding: "8px 16px",
        textDecoration: "none",
      }}
    >
      {t("Home")}
    </CustomNavLinkSmall>
  );

  const ExploreMenuItem = () => (
    <CustomNavLinkSmall
      as={Link}
      to="/explore"
      style={{
        color: "#1d1d1f",
        fontWeight: "400",
        padding: "8px 16px",
        textDecoration: "none",
      }}
    >
      {t("Explore")}
    </CustomNavLinkSmall>
  );

  const StoriesMenuItem = () => (
    <CustomNavLinkSmall
      as={Link}
      to="/stories"
      style={{
        color: "#1d1d1f",
        fontWeight: "400",
        padding: "8px 16px",
        textDecoration: "none",
      }}
    >
      {t("Stories")}
    </CustomNavLinkSmall>
  );

  return (
    <HeaderSection>
      <Container>
        <Row justify="space-between" align="middle">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2rem",
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            <LogoContainer to="/" aria-label="homepage">
              <img
                src="/favicon.svg"
                alt="CapCam Logo"
                width="36"
                height="36"
                style={{ marginRight: "10px", flexShrink: 0 }}
              />
              <LogoTitle>{getBrandName()}</LogoTitle>
            </LogoContainer>
            <NotHidden>
              <HomeMenuItem />
              <ExploreMenuItem />
              <StoriesMenuItem />
            </NotHidden>
          </div>
          <NotHidden>
            <AuthEntry />
            <LanguageToggleButton
              type="button"
              onClick={switchLanguage}
              aria-label={t("switchLanguage")}
            >
              <LanguageIcon />
            </LanguageToggleButton>
          </NotHidden>
          <MobileCluster>
            <Burger onClick={openDrawer} aria-label="Open menu">
              <Outline />
            </Burger>
          </MobileCluster>
        </Row>
        <LoginModal open={loginOpen} onClose={closeLogin} />
      </Container>
      {createPortal(
        <>
          <DrawerMask
            $open={visible}
            onClick={closeDrawer}
            aria-hidden="true"
          />
          <DrawerPanel
            $open={visible}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <DrawerHeader>
              <DrawerClose
                type="button"
                onClick={closeDrawer}
                aria-label="Close menu"
              >
                <CloseOutlined />
              </DrawerClose>
            </DrawerHeader>
            <DrawerNav>
              {DRAWER_NAV_ITEMS.map(({ to, labelKey }) => (
                <DrawerNavLink
                  key={to}
                  as={Link}
                  to={to}
                  onClick={closeDrawer}
                >
                  {t(labelKey)}
                </DrawerNavLink>
              ))}
              <DrawerNavLink
                as="button"
                type="button"
                onClick={switchLanguage}
                aria-label={t("switchLanguage")}
              >
                <LanguageIcon />
              </DrawerNavLink>
              {auth.isAuthenticated ? (
                <DrawerNavLink as={Link} to="/me" onClick={closeDrawer}>
                  {t("profileMyProfile")}
                </DrawerNavLink>
              ) : (
                <DrawerLoginButton type="button" onClick={openLogin}>
                  {t("Log in")}
                </DrawerLoginButton>
              )}
            </DrawerNav>
          </DrawerPanel>
        </>,
        document.body,
      )}
    </HeaderSection>
  );
};

export default withTranslation()(Header);
